import type { Ref } from 'vue';
import type { BlockEditorRootContext } from '#layers/block-engine/composables/block-editor/root-context';
import type { BlockEditorSelection } from '#layers/block-engine/composables/block-editor/selection';
import type { useBlockUpdate } from '#layers/block-engine/composables/block-editor/update';
import type { BlockSerialized } from '#layers/block-engine/types/block';
import { createEventHook } from '@vueuse/core';
import { reactive, ref, shallowRef } from 'vue';
import { useBlockLibraryStore } from '#layers/block-engine/composables/block-editor/library';
import { processAddBlock } from '#layers/block-engine/composables/block-editor/utils/process-add-block';
import { getBlockConfig, useBlockConfigApi } from '#layers/block-engine/utils/block/api/config';
import { slotOutletBlockId } from '#layers/block-engine/utils/block/const';
import { getBlockInitialProps } from '#layers/block-engine/utils/block/default-props';
import { isBlockDefinition } from '#layers/block-engine/utils/block/editor';

interface BlockEditorInsertTarget {
	id: string;
	childId?: string;
	slotName?: string;
	index: number;
}

interface BlockAddParams {
	blockId?: string;
	props?: Record<string, any>;
	target?: BlockEditorInsertTarget;
	/**
	 * @default true
	 */
	autoSelect?: boolean;
}

export function useBlockEditorInsertion({
	blockData,
	updateBlock,
	selection,
	rootContext,
	translateString,
}: {
	blockData: Ref<BlockSerialized | null>;
	updateBlock: ReturnType<typeof useBlockUpdate>['updateBlock'];
	selection: BlockEditorSelection;
	rootContext: BlockEditorRootContext;
	/** Translate string defaults before serializing a newly inserted block. */
	translateString: (value: string) => string;
}) {
	const { getBlock } = useBlockLibraryStore();
	const target = shallowRef<BlockEditorInsertTarget | null>(null);
	const blockId = ref<string | null>(null);
	const hoveredTargetId = ref<string | null>(null);
	const hoveredTargetDistance = reactive<{ distance: number; id: string | null }>({ distance: 0, id: null });
	const clearEvent = createEventHook();

	function clear() {
		target.value = null;
		blockId.value = null;
		clearEvent.trigger();
	}

	async function add(params: BlockAddParams = {}) {
		let newChildId: string | null = null;

		const _blockId = params.blockId ?? blockId.value;

		if (blockData.value) {
			const props: Record<string, any> = {};

			const _target = params.target ?? target.value;

			if (_target && _blockId) {
				const addedBlock = getBlock(_blockId);
				const blockConfigApiBase = useBlockConfigApi(undefined, {
					getParent: () => {
						if (!_target.childId) return;
						return blockData.value?.children?.[_target.childId];
					},
					rootContext,
				});

				if (isBlockDefinition(addedBlock)) {
					// New blocks need both Vue prop defaults and form-schema option defaults
					// before explicit insertion props are layered on top.
					Object.assign(props, getBlockInitialProps({
						block: addedBlock,
						context: blockConfigApiBase,
						props: params.props,
						translateString,
					}));
				}
				else if (params.props) {
					Object.assign(props, params.props);
				}

				// onInserted hook
				if (isBlockDefinition(addedBlock)) {
					const config = getBlockConfig(addedBlock);

					if (config?.onInserted) {
						await config.onInserted({
							...blockConfigApiBase,
							getProps: () => props,
							setProps: (value) => {
								Object.assign(props, value);
							},
						});
					}
				}

				const { newId, data } = processAddBlock({
					blockId: _blockId,
					index: _target.index,
					parentChildId: _target.childId,
					slot: _target.slotName,
					editedBlock: blockData.value,
					props,
				});

				if (params.autoSelect !== false) {
					selection.selectedChildIds.value = [newId];
				}

				newChildId = newId;

				// Add to the slots
				if (_blockId === slotOutletBlockId) {
					data.slots = blockData.value.slots ?? [];

					if (!data.slots.some((s) => s.name === 'default')) {
						data.slots.push({
							name: 'default',
							props: [],
						});
					}
				}

				updateBlock(data);
			}
		}

		// Insertion cleared when the new block is loaded
		// See ../BlockEditor/BlockEditorRenderChild.vue
		if (_blockId === slotOutletBlockId) {
			clear();
		}

		return newChildId;
	}

	return {
		target,
		blockId,
		hoveredTargetId,
		hoveredTargetDistance,
		clearEvent,
		clear,
		add,
	};
}

export type BlockEditorInsertion = ReturnType<typeof useBlockEditorInsertion>;
